/**
 * Connects to the chat namespace as staff so we receive chat:unread_updated in real time.
 * When the backend emits unread_updated (after any message is sent), we dispatch chat:activity
 * so the Layout refetches group unread and ChatsPage can refetch customer list.
 * No polling delay – counts update immediately when a message is sent.
 */

import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { SOCKET_IO_URL } from '../lib/utils';
import { ensureNotificationPermission, notifyIncoming, playBeep } from '../lib/notify';

const CHAT_NAMESPACE = '/chat';

export default function useStaffUnreadSocket({ enabled = true, token } = {}) {
  const socketRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      socketRef.current = null;
      return undefined;
    }

    const authToken =
      token ||
      (typeof window !== "undefined"
        ? localStorage.getItem("company_admin_token")
        : null);
    if (!authToken) {
      return undefined;
    }

    let cancelled = false;
    const socket = io(`${SOCKET_IO_URL}${CHAT_NAMESPACE}`, {
      auth: { token: authToken },
      query: { token: authToken },
      path: '/api/socket.io',
      transports: ['polling', 'websocket'],
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });

    socketRef.current = socket;
    ensureNotificationPermission().catch(() => {});

    const bumpUnread = () => {
      if (cancelled) return;
      try {
        window.dispatchEvent(new Event('chat:activity'));
      } catch (e) {
        // ignore
      }
    };

    socket.on('connect', () => {
      if (cancelled) return;
      try {
        window.dispatchEvent(
          new CustomEvent('appointment:activity', { detail: { event: 'reconnect' } }),
        );
      } catch (e) {
        // ignore
      }
    });

    socket.on('chat:unread_updated', bumpUnread);
    socket.on('chat:message', bumpUnread);

    socket.on('chat:appointment_updated', (payload) => {
      try {
        window.dispatchEvent(
          new CustomEvent('appointment:activity', { detail: payload || {} }),
        );
      } catch (e) {
        // ignore
      }
      if (payload?.event === 'booked') {
        const name = payload.customer_name || 'A customer';
        const kind = payload.type === 'call' ? 'call' : 'chat';
        toast.info(`${name} booked a ${kind} appointment`, {
          action: {
            label: 'Open',
            onClick: () => {
              window.location.href = payload?.appointment_id
                ? `/admin/appointments?id=${payload.appointment_id}`
                : '/admin/appointments';
            },
          },
        });
      }
    });

    socket.on('chat:appointment_call_reminder', (payload) => {
      const name = payload?.customer_name || 'a customer';
      const title = 'Appointment in 5 minutes';
      const body = `Call with ${name} starts in 5 minutes`;
      playBeep();
      notifyIncoming({ title, body });
      toast.info(body, {
        action: {
          label: 'Open',
          onClick: () => {
            window.location.href = payload?.appointment_id
              ? `/admin/appointments?id=${payload.appointment_id}`
              : '/admin/appointments';
          },
        },
      });
    });

    socket.on('chat:appointment_call_waiting', (payload) => {
      const name = payload?.customer_name || 'your customer';
      const title = 'Join the meeting';
      const body = `Waiting for you to join the meeting with ${name}`;
      playBeep();
      notifyIncoming({ title, body });
      toast.warning(body, {
        duration: 12000,
        action: {
          label: 'Join',
          onClick: () => {
            window.location.href = payload?.appointment_id
              ? `/admin/appointments?id=${payload.appointment_id}`
              : '/admin/appointments';
          },
        },
      });
      try {
        window.dispatchEvent(
          new CustomEvent('appointment:activity', { detail: payload || {} }),
        );
      } catch (e) {
        // ignore
      }
    });

    socket.on('chat:appointment_call_ready', (payload) => {
      try {
        window.dispatchEvent(
          new CustomEvent('appointment:activity', { detail: payload || {} }),
        );
      } catch (e) {
        // ignore
      }
    });

    socket.on('chat:call_ended', (payload) => {
      try {
        window.dispatchEvent(
          new CustomEvent('media-call:ended', { detail: payload || {} }),
        );
      } catch (e) {
        // ignore
      }
    });

    socket.on('chat:appointment_call_ended', (payload) => {
      try {
        window.dispatchEvent(
          new CustomEvent('media-call:ended', { detail: payload || {} }),
        );
        window.dispatchEvent(
          new CustomEvent('appointment:activity', { detail: payload || {} }),
        );
      } catch (e) {
        // ignore
      }
    });

    return () => {
      cancelled = true;
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, token]);

  return {};
}
